"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.GridTable = exports.setGridTableDefaults = exports.setDefaultStyle = exports.setRunningInJest = void 0;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const memoize_one_1 = __importDefault(require("memoize-one"));
const mobx_1 = require("mobx");
const react_1 = __importStar(require("react"));
const react_virtuoso_1 = require("react-virtuoso");
const components_1 = require("..");
const PresentationContext_1 = require("../PresentationContext");
const GridTableApi_1 = require("./GridTableApi");
const useSetupColumnSizes_1 = require("./hooks/useSetupColumnSizes");
const TableStyles_1 = require("./TableStyles");
const columns_1 = require("./utils/columns");
const TableState_1 = require("./utils/TableState");
const utils_1 = require("./utils/utils");
const Css_1 = require("../../Css");
const hooks_1 = require("../../hooks");
const useRenderCount_1 = require("../../hooks/useRenderCount");
const utils_2 = require("../../utils");
const Row_1 = require("./components/Row");
const RowState_1 = require("./utils/RowState");
let runningInJest = false;
/** Tells GridTable we're running in Jest, which forces as=virtual to be as=div, to work in jsdom. */
function setRunningInJest() {
    runningInJest = true;
}
exports.setRunningInJest = setRunningInJest;
let defaults = {
    style: TableStyles_1.defaultStyle,
    stickyHeader: false,
};
/** Configures the default/app-wide GridStyle. */
function setDefaultStyle(style) {
    defaults.style = style;
}
exports.setDefaultStyle = setDefaultStyle;
/** Configures the default/app-wide GridTable settings. */
function setGridTableDefaults(opts) {
    defaults = { ...defaults, ...opts };
}
exports.setGridTableDefaults = setGridTableDefaults;
/**
 * Renders data in our table layout.
 *
 * Tables are essentially a matrix of columns and rows, and this `GridTable` API is setup
 * such that:
 *
 * - Rows are mostly data, tagged with a given `kind`
 *   - I.e. this handles tables with nested/non-homogeneous rows because you can have a
 *     row with `kind: "parent"` and another with `kind: "child"`
 * - Columns are mostly rendering logic
 *   - I.e. each column defines it's behavior for each given row `kind`
 *
 * In a "kind" of cute way, headers are not modeled specially, i.e. they are just another
 * row `kind` along with the data rows. (Admittedly, out of pragmatism, we do apply some
 * special styling to the row that uses `kind: "header"`.)
 *
 * For some rationale of our current/historical rendering approaches, see the following doc:
 *
 * https://docs.google.com/document/d/1DFnlkDubK4nG_GLf_hB8yp0flnSNt_3IBh5iOicuaFM/edit#heading=h.9m9cpwgeqfc9
 */
function GridTable(props) {
    var _a, _b, _c;
    const { id = "gridTable", as = "div", columns: _columns, rows, style: maybeStyle = defaults.style, rowStyles, stickyHeader = defaults.stickyHeader, stickyOffset = 0, xss, filter, filterMaxRows, fallbackMessage = "No rows found.", infoMessage, persistCollapse, resizeTarget, activeRowId, activeCellId, visibleColumnsStorageKey, infiniteScroll, onRowSelect, onRowDrop: droppedCallback, } = props;
    const columnsWithIds = (0, react_1.useMemo)(() => (0, columns_1.assignDefaultColumnIds)(_columns), [_columns]);
    // We only use this in as=virtual mode, but keep this here for rowLookup to use
    const virtuosoRef = (0, react_1.useRef)(null);
    // Use this ref to watch for changes in the GridTable's container and resize columns accordingly.
    const resizeRef = (0, react_1.useRef)(null);
    const api = (0, react_1.useMemo)(() => {
        var _a;
        // Let the user pass in their own api handle, otherwise make our own
        const api = (_a = props.api) !== null && _a !== void 0 ? _a : new GridTableApi_1.GridTableApiImpl();
        api.init(persistCollapse, virtuosoRef);
        api.setActiveRowId(activeRowId);
        api.setActiveCellId(activeCellId);
        // Push the initial columns directly into tableState, b/c that is what
        // makes the tests pass, but then further updates we'll do through useEffect
        // to avoid "Cannot update component during render" errors.
        api.tableState.setColumns(columnsWithIds, visibleColumnsStorageKey);
        return api;
    }, 
    // TODO: validate this eslint-disable. It was automatically ignored as part of https://app.shortcut.com/homebound-team/story/40033/enable-react-hooks-exhaustive-deps-for-react-projects
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.api]);
    const [draggedRow, _setDraggedRow] = (0, react_1.useState)(undefined);
    const draggedRowRef = (0, react_1.useRef)(draggedRow);
    const setDraggedRow = (row) => {
        draggedRowRef.current = row;
        _setDraggedRow(row);
    };
    const style = (0, TableStyles_1.resolveStyles)(maybeStyle);
    const { tableState } = api;
    tableState.onRowSelect = onRowSelect;
    // Use a single useEffect to push props into the TableState observable, that
    // way we avoid React warnings when the observable mutations cause downstream
    // components to be marked for re-render. Mobx will ignore setter calls that
    // don't actually change the value, so we can do this in a single useEffect.
    (0, react_1.useEffect)(() => {
        // Use runInAction so mobx delays any reactions until all the mutations happen
        (0, mobx_1.runInAction)(() => {
            tableState.setRows(rows);
            tableState.setColumns(columnsWithIds, visibleColumnsStorageKey);
            tableState.setSearch(filter);
            tableState.activeRowId = activeRowId;
            tableState.activeCellId = activeCellId;
        });
    }, [tableState, rows, columnsWithIds, visibleColumnsStorageKey, activeRowId, activeCellId, filter]);
    const columns = (0, hooks_1.useComputed)(() => {
        return tableState.visibleColumns;
    }, [tableState]);
    // Initialize the sort state. This will only happen on the first render.
    // Once the `TableState.sort` is defined, it will not re-initialize.
    tableState.initSortState(props.sorting, columns);
    // We track render count at the table level, which seems odd (we should be able to track this
    // internally within each GridRow using a useRef), but we have suspicions that react-virtuoso
    // (or us) is resetting component state more than necessary, so we track render counts from
    // here instead.
    const { getCount } = (0, useRenderCount_1.useRenderCount)();
    // Our column sizes use either `w` or `expandedWidth`, so see which columns are currently expanded
    const expandedColumnIds = (0, hooks_1.useComputed)(() => tableState.expandedColumnIds, [tableState]);
    const columnSizes = (0, useSetupColumnSizes_1.useSetupColumnSizes)(style, columns, resizeTarget !== null && resizeTarget !== void 0 ? resizeTarget : resizeRef, expandedColumnIds);
    // allows us to unset children and grandchildren, etc.
    function recursiveSetDraggedOver(rows, draggedOver) {
        rows.forEach((r) => {
            tableState.maybeSetRowDraggedOver(r.id, draggedOver);
            if (r.children) {
                recursiveSetDraggedOver(r.children, draggedOver);
            }
        });
    }
    function onDragStart(row, evt) {
        if (!row.draggable || !droppedCallback) {
            return;
        }
        evt.dataTransfer.effectAllowed = "move";
        evt.dataTransfer.setData("text/plain", JSON.stringify({ row }));
        setDraggedRow(row);
    }
    const onDragEnd = (row, evt) => {
        if (!row.draggable || !droppedCallback) {
            return;
        }
        evt.preventDefault();
        recursiveSetDraggedOver(rows, RowState_1.DraggedOver.None);
    };
    const onDrop = (row, evt) => {
        if (!row.draggable || !droppedCallback) {
            return;
        }
        evt.preventDefault();
        if (droppedCallback) {
            recursiveSetDraggedOver(rows, RowState_1.DraggedOver.None);
            try {
                const draggedRowData = JSON.parse(evt.dataTransfer.getData("text/plain")).row;
                if (draggedRowData.id === row.id) {
                    return;
                }
                const isBelow = (0, utils_1.isCursorBelowMidpoint)(evt.currentTarget, evt.clientY);
                droppedCallback(draggedRowData, row, isBelow ? 1 : 0);
            }
            catch (e) {
                console.error(e.message, e.stack);
            }
        }
    };
    const onDragEnter = (row, evt) => {
        if (!row.draggable || !droppedCallback) {
            return;
        }
        evt.preventDefault();
        // set flags for css spacer
        // don't set none for the row we are entering
        recursiveSetDraggedOver(rows.filter((r) => r.id !== row.id), RowState_1.DraggedOver.None);
        if (draggedRowRef.current) {
            if (draggedRowRef.current.id === row.id) {
                return;
            }
            // determine above or below
            const isBelow = (0, utils_1.isCursorBelowMidpoint)(evt.currentTarget, evt.clientY);
            tableState.maybeSetRowDraggedOver(row.id, isBelow ? RowState_1.DraggedOver.Below : RowState_1.DraggedOver.Above, draggedRowRef.current);
        }
    };
    const onDragOver = (row, evt) => {
        if (!row.draggable || !droppedCallback) {
            return;
        }
        evt.preventDefault();
        if (draggedRowRef.current) {
            if (draggedRowRef.current.id === row.id || !evt.currentTarget) {
                tableState.maybeSetRowDraggedOver(row.id, RowState_1.DraggedOver.None, draggedRowRef.current);
                return;
            }
            // continuously determine above or below
            const isBelow = (0, utils_1.isCursorBelowMidpoint)(evt.currentTarget, evt.clientY);
            tableState.maybeSetRowDraggedOver(row.id, isBelow ? RowState_1.DraggedOver.Below : RowState_1.DraggedOver.Above, draggedRowRef.current);
        }
    };
    // Flatten, hide-if-filtered, hide-if-collapsed, and component-ize the sorted rows.
    const [tableHeadRows, visibleDataRows, keptSelectedRows, tooManyClientSideRows] = (0, hooks_1.useComputed)(() => {
        // Split out the header rows from the data rows so that we can put an `infoMessage` in between them (if needed).
        const headerRows = [];
        const expandableHeaderRows = [];
        const totalsRows = [];
        const keptSelectedRows = [];
        let visibleDataRows = [];
        const { visibleRows } = tableState;
        const hasExpandableHeader = visibleRows.some((rs) => rs.row.id === utils_1.EXPANDABLE_HEADER);
        // Get the flat list or rows from the header down...
        visibleRows.forEach((rs) => {
            const row = ((0, jsx_runtime_1.jsx)(Row_1.Row, { onDragStart: onDragStart, onDragOver: onDragOver, onDragEnd: onDragEnd, onDrop: onDrop, onDragEnter: onDragEnter, ...{
                    as,
                    rs,
                    style,
                    rowStyles,
                    columnSizes,
                    getCount,
                    cellHighlight: "cellHighlight" in maybeStyle && maybeStyle.cellHighlight === true,
                    omitRowHover: "rowHover" in maybeStyle && maybeStyle.rowHover === false,
                    hasExpandableHeader,
                } }, rs.key));
            if (rs.kind === "header") {
                headerRows.push(row);
            }
            else if (rs.kind === "expandableHeader") {
                expandableHeaderRows.push(row);
            }
            else if (rs.kind === "totals") {
                totalsRows.push(row);
            }
            else if (rs.isKept || rs.kind === utils_1.KEPT_GROUP) {
                keptSelectedRows.push(row);
            }
            else {
                visibleDataRows.push(row);
            }
        });
        // Once our header rows are created we can organize them in expected order.
        const tableHeadRows = expandableHeaderRows.concat(headerRows).concat(totalsRows);
        const tooManyClientSideRows = !!filterMaxRows && visibleDataRows.length > filterMaxRows;
        if (tooManyClientSideRows) {
            visibleDataRows = visibleDataRows.slice(0, filterMaxRows + keptSelectedRows.length);
        }
        return [tableHeadRows, visibleDataRows, keptSelectedRows, tooManyClientSideRows];
    }, [as, api, style, rowStyles, maybeStyle, columnSizes, getCount, filterMaxRows]);
    // Push back to the caller a way to ask us where a row is.
    // Refs are cheap to assign to, so we don't bother doing this in a useEffect
    if (props.rowLookup)
        props.rowLookup.current = api.lookup;
    const noData = visibleDataRows.length === 0;
    const firstRowMessage = (noData && fallbackMessage) || (tooManyClientSideRows && "Hiding some rows, use filter...") || infoMessage;
    const borderless = (_a = style === null || style === void 0 ? void 0 : style.presentationSettings) === null || _a === void 0 ? void 0 : _a.borderless;
    const typeScale = (_b = style === null || style === void 0 ? void 0 : style.presentationSettings) === null || _b === void 0 ? void 0 : _b.typeScale;
    const fieldProps = (0, react_1.useMemo)(() => ({
        labelStyle: "hidden",
        numberAlignment: "right",
        compact: true,
        errorInTooltip: true,
        // Avoid passing `undefined` as it will unset existing PresentationContext settings
        ...(borderless !== undefined ? { borderless } : {}),
        ...(typeScale !== undefined ? { typeScale } : {}),
    }), [borderless, typeScale]);
    // If we're running in Jest, force using `as=div` b/c jsdom doesn't support react-virtuoso.
    // This enables still putting the application's business/rendering logic under test, and letting it
    // just trust the GridTable impl that, at runtime, `as=virtual` will (other than being virtualized)
    // behave semantically the same as `as=div` did for its tests.
    const _as = as === "virtual" && runningInJest ? "div" : as;
    const rowStateContext = (0, react_1.useMemo)(() => ({ tableState: tableState }), [tableState]);
    return ((0, jsx_runtime_1.jsx)(TableState_1.TableStateContext.Provider, { value: rowStateContext, children: (0, jsx_runtime_1.jsxs)(PresentationContext_1.PresentationProvider, { fieldProps: fieldProps, wrap: (_c = style === null || style === void 0 ? void 0 : style.presentationSettings) === null || _c === void 0 ? void 0 : _c.wrap, children: [(0, jsx_runtime_1.jsx)("div", { ref: resizeRef, css: (0, components_1.getTableRefWidthStyles)(as === "virtual") }), renders[_as](style, id, columns, visibleDataRows, keptSelectedRows, firstRowMessage, stickyHeader, xss, virtuosoRef, tableHeadRows, stickyOffset, infiniteScroll)] }) }));
}
exports.GridTable = GridTable;
// Determine which HTML element to use to build the GridTable
const renders = {
    table: renderTable,
    div: renderDiv,
    virtual: renderVirtual,
};
/** Renders table using divs with flexbox rows, which is the default render */
function renderDiv(style, id, columns, visibleDataRows, keptSelectedRows, firstRowMessage, stickyHeader, xss, _virtuosoRef, tableHeadRows, stickyOffset, _infiniteScroll) {
    return ((0, jsx_runtime_1.jsxs)("div", { css: {
            // Use `fit-content` to ensure the width of the table takes up the full width of its content.
            // Otherwise, the table's width would be that of its container, which may not be as wide as the table itself.
            // In cases where we have sticky columns on a very wide table, then the container which the columns "stick" to (which is the table),
            // needs to be as wide as the table's content, or else we lose the "stickiness" once scrolling past width of the table's container.
            ...Css_1.Css.mw("fit-content").$,
            ...style.rootCss,
            ...(style.minWidthPx ? Css_1.Css.mwPx(style.minWidthPx).$ : {}),
            ...xss,
        }, "data-testid": id, children: [(0, jsx_runtime_1.jsx)("div", { css: {
                    ...(style.firstRowCss && Css_1.Css.addIn("& > div:first-of-type", style.firstRowCss).$),
                    ...Css_1.Css.if(stickyHeader).sticky.topPx(stickyOffset).z(utils_1.zIndices.stickyHeader).$,
                }, children: tableHeadRows }), (0, jsx_runtime_1.jsxs)("div", { css: {
                    ...(style.betweenRowsCss ? Css_1.Css.addIn(`& > div > *`, style.betweenRowsCss).$ : {}),
                    ...(style.firstNonHeaderRowCss ? Css_1.Css.addIn(`& > div:first-of-type`, style.firstNonHeaderRowCss).$ : {}),
                    ...(style.lastRowCss && Css_1.Css.addIn("& > div:last-of-type", style.lastRowCss).$),
                }, children: [keptSelectedRows, firstRowMessage && ((0, jsx_runtime_1.jsx)("div", { css: { ...style.firstRowMessageCss }, "data-gridrow": true, children: firstRowMessage })), visibleDataRows] })] }));
}
/** Renders as a table, primarily/solely for good print support. */
function renderTable(style, id, columns, visibleDataRows, keptSelectedRows, firstRowMessage, stickyHeader, xss, _virtuosoRef, tableHeadRows, stickyOffset, _infiniteScroll) {
    return ((0, jsx_runtime_1.jsxs)("table", { css: {
            ...Css_1.Css.w100.add("borderCollapse", "separate").add("borderSpacing", "0").$,
            ...Css_1.Css.addIn("& > tbody > tr > * ", style.betweenRowsCss || {})
                // removes border between header and second row
                .addIn("& > tbody > tr:first-of-type", style.firstNonHeaderRowCss || {}).$,
            ...Css_1.Css.addIn("& > tbody > tr:last-of-type", style.lastRowCss).$,
            ...Css_1.Css.addIn("& > thead > tr:first-of-type", style.firstRowCss).$,
            ...style.rootCss,
            ...(style.minWidthPx ? Css_1.Css.mwPx(style.minWidthPx).$ : {}),
            ...xss,
        }, "data-testid": id, children: [(0, jsx_runtime_1.jsx)("thead", { css: Css_1.Css.if(stickyHeader).sticky.topPx(stickyOffset).z(utils_1.zIndices.stickyHeader).$, children: tableHeadRows }), (0, jsx_runtime_1.jsxs)("tbody", { children: [keptSelectedRows, firstRowMessage && ((0, jsx_runtime_1.jsx)("tr", { children: (0, jsx_runtime_1.jsx)("td", { colSpan: columns.length, css: { ...style.firstRowMessageCss }, children: firstRowMessage }) })), visibleDataRows] })] }));
}
/**
 * Uses react-virtuoso to render rows virtually.
 *
 * It seems like react-virtuoso is the only one that can do _measured_ variable
 * sizes. I.e. react-window's variable list let's you provide a size, but it's
 * a size you lookup, not one that is measured from the DOM, see [1].
 *
 * I also tried react-virtual, which is headless and really small, but a) the
 * `measureRef` approach seems buggy [2] and b) rows were getting re-rendered
 * maybe due to [3] and they have no examples showing memoization, which is
 * concerning.
 *
 * react-virtuoso also seems like the most maintained (react-window is no
 * longer being actively worked on) and featureful library (like sticky headers),
 * so going with that for now.
 *
 * [1]: https://github.com/bvaughn/react-window/issues/6
 * [2]: https://github.com/tannerlinsley/react-virtual/issues/85
 * [3]: https://github.com/tannerlinsley/react-virtual/issues/108
 */
function renderVirtual(style, id, columns, visibleDataRows, keptSelectedRows, firstRowMessage, stickyHeader, xss, virtuosoRef, tableHeadRows, _stickyOffset, infiniteScroll) {
    var _a;
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { footerStyle, listStyle } = (0, react_1.useMemo)(() => {
        var _a;
        const { paddingBottom, ...otherRootStyles } = (_a = style.rootCss) !== null && _a !== void 0 ? _a : {};
        return { footerStyle: { paddingBottom }, listStyle: { ...style, rootCss: otherRootStyles } };
    }, [style]);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [fetchMoreInProgress, setFetchMoreInProgress] = (0, react_1.useState)(false);
    return ((0, jsx_runtime_1.jsx)(react_virtuoso_1.Virtuoso, { overscan: 5, ref: virtuosoRef, components: {
            // Applying a zIndex: 2 to ensure it stays on top of sticky columns
            TopItemList: react_1.default.forwardRef((props, ref) => ((0, jsx_runtime_1.jsx)("div", { ...props, ref: ref, style: { ...props.style, ...{ zIndex: utils_1.zIndices.stickyHeader } } }))),
            List: VirtualRoot(listStyle, columns, id, xss),
            Footer: () => {
                return ((0, jsx_runtime_1.jsx)("div", { css: footerStyle, children: fetchMoreInProgress && ((0, jsx_runtime_1.jsx)("div", { css: Css_1.Css.h5.df.aic.jcc.$, children: (0, jsx_runtime_1.jsx)(components_1.Loader, { size: "xs" }) })) }));
            },
        }, 
        // Pin/sticky both the header row(s) + firstRowMessage to the top
        topItemCount: stickyHeader ? tableHeadRows.length : 0, itemContent: (index) => {
            // Since we have 3 arrays of rows: `tableHeadRows` and `visibleDataRows` and `keptSelectedRows` we must determine which one to render.
            if (index < tableHeadRows.length) {
                return tableHeadRows[index];
            }
            // Reset index
            index -= tableHeadRows.length;
            // Show keptSelectedRows if there are any
            if (index < keptSelectedRows.length) {
                return keptSelectedRows[index];
            }
            // Reset index
            index -= keptSelectedRows.length;
            // Show firstRowMessage as the first `filteredRow`
            if (firstRowMessage) {
                if (index === 0) {
                    return (
                    // Ensure the fallback message is the same width as the table
                    (0, jsx_runtime_1.jsx)("div", { css: (0, components_1.getTableRefWidthStyles)(true), children: (0, jsx_runtime_1.jsx)("div", { css: { ...style.firstRowMessageCss }, children: firstRowMessage }) }));
                }
                // Shift index -1 when there is a firstRowMessage to not skip the
                // first `filteredRow`
                index--;
            }
            // Lastly render the table body rows
            return visibleDataRows[index];
        }, totalCount: tableHeadRows.length + (firstRowMessage ? 1 : 0) + visibleDataRows.length + keptSelectedRows.length, ...(infiniteScroll
            ? {
                increaseViewportBy: {
                    bottom: (_a = infiniteScroll.endOffsetPx) !== null && _a !== void 0 ? _a : 500,
                    top: 0,
                },
                // Add a `index > 0` check b/c Virtuoso is calling this in storybook
                // with `endReached(0)` at odd times, like on page unload/story load,
                // which then causes our test data to have duplicate ids in it.
                endReached: (index) => {
                    if (index === 0)
                        return;
                    const result = infiniteScroll.onEndReached(index);
                    if ((0, utils_2.isPromise)(result)) {
                        setFetchMoreInProgress(true);
                        result.finally(() => setFetchMoreInProgress(false));
                    }
                },
            }
            : {}) }));
}
/**
 * A table might render two of these components to represent two virtual lists.
 * This generally happens when `topItemCount` prop is used and React-Virtuoso
 * creates to Virtual lists where the first represents, generally, the header
 * rows and the second represents the non-header rows (list rows).
 *
 * The main goal of this custom component is to:
 * - Customize the list wrapper to our styles
 *
 * We wrap this in memoizeOne so that React.createElement sees a
 * consistent/stable component identity, even though technically we have a
 * different "component" per the given set of props (solely to capture as
 * params that we can't pass through react-virtuoso's API as props).
 */
const VirtualRoot = (0, memoize_one_1.default)((gs, _columns, id, xss) => {
    return react_1.default.forwardRef(function VirtualRoot({ style, children }, ref) {
        // This VirtualRoot list represent the header when no styles are given. The
        // table list generally has styles to scroll the page for windowing.
        const isHeader = Object.keys(style || {}).length === 0;
        // This re-renders each time we have new children in the viewport
        return ((0, jsx_runtime_1.jsx)("div", { ref: ref, style: { ...style, ...{ minWidth: "fit-content" } }, css: {
                // Add an extra `> div` due to Item + itemContent both having divs
                ...Css_1.Css.addIn("& > div + div > div > *", gs.betweenRowsCss || {}).$,
                // Table list styles only
                ...(isHeader
                    ? Css_1.Css.addIn("& > div:first-of-type > *", gs.firstRowCss).$
                    : {
                        ...Css_1.Css.addIn("& > div:first-of-type", gs.firstNonHeaderRowCss).$,
                        ...Css_1.Css.addIn("& > div:last-of-type > *", gs.lastRowCss).$,
                    }),
                ...gs.rootCss,
                ...(gs.minWidthPx ? Css_1.Css.mwPx(gs.minWidthPx).$ : {}),
                ...xss,
            }, "data-testid": id, children: children }));
    });
});
